import React, { useRef, useState, useEffect } from 'react';
import { useTheme } from 'native-base';
import { Center, Divider, FlatList, VStack, Pressable, Text, Box } from 'native-base';
import { Dimensions } from 'react-native';
import PrivateMessage from './PrivateMessage';
import GoBackButton from '../content/common/GoBackButton';
import CommunicationPreferencesButton from '../content/common/reusables/CommunicationPreferencesButton';
import CreateMessageBox from './CreateMessageBox';
import Banner from '../content/common/reusables/Banner';
import { sentenceJoinUserNames, toggleUserDMOption, uniqueReplyReducer } from '../utils/helpers';
import { useEnvState } from '../../context/env';
import { useAuthState, useAuthDispatch, authUtil } from '../../context/auth';
import { useOverviewDispatch, useOverviewState } from '../../context/overview';
import { useMessagesState, useMessagesDispatch } from '../../context/messages';
import { useMessageBusState } from '../../context/messagebus';
import { useMessageBus } from '../hooks/useMessageBus';
import { topicsAPI } from '../../context/topics';
import SearchRecipient from './SearchRecipient';
import { LANGUAGES, MAX_REPLIES_PER_BATCH } from '../Constants';
import { useEditPostDispatch, useEditPostState } from '../../context/edit';

/* TODO: Similar logic to SingleTopicScreen/Replies... could maybe do some consolidation...?
 *    I don't think either one is clever in grabbing from the message bus and adding a new reply to the post_stream
 */
const PrivateMessageScreen = props => {
  var _props$route, _props$route2;
  const theme = useTheme(),
    {
      colors
    } = theme;
  const PMRef = useRef();
  const [privateMessages, setPrivateMessages] = useState([]);
  const [topic, setTopic] = useState(null);
  const [privateMessageId, setPrivateMessageId] = useState((_props$route = props.route) === null || _props$route === void 0 || (_props$route = _props$route.params) === null || _props$route === void 0 ? void 0 : _props$route.topicId);
  const envState = useEnvState();
  const authState = useAuthState();
  const authDispatch = useAuthDispatch();
  const messagesState = useMessagesState();
  const messagesDispatch = useMessagesDispatch();
  const messageBusState = useMessageBusState();
  const overviewState = useOverviewState();
  const overviewDispatch = useOverviewDispatch();
  const [dmUsers, setDMUsers] = useState(messagesState.message_users);
  const [topicTitle, setTopicTitle] = useState('');
  const [repliesStream, setRepliesStream] = useState([]);
  const [moreReplies, setMoreReplies] = useState(true);
  const [topicIdListForReplies, setTopicIdListForReplies] = useState([]);

  // this is shared with the ForumHomeScreen, so we should already be initialized!
  const {
    isInitialized,
    isListening,
    getUniqueList,
    refreshTopic,
    closeReplyBanner
  } = useMessageBus({
    userAuthenticated: authUtil.userAuthed(authState)
  });
  const editPostDispatch = useEditPostDispatch();
  const {
    postUpdated,
    loadingNewReply
  } = useEditPostState();
  const scrollToBottom = useRef(false);
  useEffect(() => {
    if (props.dispatchForumAuth) {
      toggleUserDMOption({
        authState,
        envState,
        authDispatch
      });
    }
  }, [props.dispatchForumAuth]);
  useEffect(() => {
    setTopicIdListForReplies(getUniqueList(messageBusState.replies));
  }, [messageBusState.replies]);
  const refreshReplyBanner = () => {
    refreshTopic(privateMessageId);
    // lazy case, we have to make an API call again anyway so just call again
    getPrivateMessageTopic();
    // let's set notification to read since we are here
    // overwrite the messages to be read
    overviewDispatch({
      type: 'SET_NOTIFICATIONS',
      payload: {
        message: overviewState.notifications.message.map(item => {
          return {
            ...item,
            read: item.topic_id === privateMessageId ? true : item.read
          };
        })
      }
    });
    overviewDispatch({
      type: 'SET_MESSAGES',
      payload: overviewState.messages
    }); //reset messages with read notification
    overviewDispatch({
      type: 'SET_UNREAD_MESSAGES_COUNT',
      payload: overviewState.unreadMessagesCount - 1
    });
  };
  useEffect(() => {
    const fetchMessages = async () => {
      await getPrivateMessageTopic();
    };
    fetchMessages();
  }, [privateMessageId]);
  useEffect(() => {
    updateTopicTitle();
  }, [dmUsers]);
  const updateTopicTitle = () => {
    setTopicTitle(`Message between ${sentenceJoinUserNames({
      authState,
      users: dmUsers,
      includeSelf: true
    })}`);
  };
  const getPrivateMessageTopic = async () => {
    try {
      let response = {};
      if (privateMessageId) {
        var _response, _response2, _response3;
        const lastPage = true;
        response = await topicsAPI.getSingleTopic(envState.base_url, authState.api_key, privateMessageId, lastPage);
        const post_stream = (_response = response) === null || _response === void 0 ? void 0 : _response.post_stream;
        if (post_stream) {
          const sortedMessages = post_stream.posts.sort((a, b) => Date(b.created_at) - Date(a.created_at));
          setRepliesStream(post_stream.stream);
          setTopic(response);
          setPrivateMessages(sortedMessages);
          setMoreReplies(post_stream.stream.length > sortedMessages.length);
          scrollToBottom.current = true;
        }

        // add/update this topic in the overview state
        const existingMessages = overviewState.messages.filter(m => m.topic_id !== privateMessageId);
        overviewDispatch({
          type: 'SET_MESSAGES',
          payload: [{
            ...response,
            allowed_user_summary: (_response2 = response) === null || _response2 === void 0 || (_response2 = _response2.details) === null || _response2 === void 0 ? void 0 : _response2.allowed_users.map(u => u.username),
            participants: (_response3 = response) === null || _response3 === void 0 || (_response3 = _response3.details) === null || _response3 === void 0 ? void 0 : _response3.allowed_users.map(u => {
              return {
                username: u.username
              };
            }),
            topic_id: privateMessageId
          }, ...existingMessages]
        });
      }
      return response;
    } catch (err) {
      console.log('Error while getting private messages', err);
      return {};
    }
  };
  const loadMoreReplies = async () => {
    // figure out the next batch of replies to get
    const loadedReplyIDs = privateMessages.map(r => r.id);
    let remainingReplies = repliesStream.filter(s => !loadedReplyIDs.includes(s));
    if (remainingReplies.length > 0) {
      const nextBatch = remainingReplies.slice(0, MAX_REPLIES_PER_BATCH);
      try {
        const response = await topicsAPI.getSingleTopicMoreReplies(envState.base_url, authState.api_key, privateMessageId, nextBatch);
        if (response && response.post_stream) {
          const {
            posts
          } = response.post_stream;
          if (posts) {
            /* even though uniqueReplyReducer also does a sort
             * we still have to do this because the API call above
             * returns us the first post, so we have to shave it back off */
            const sortedReplies = posts.sort((a, b) => a.post_number - b.post_number);
            let sortedRepliesSoFar = uniqueReplyReducer([...privateMessages, ...sortedReplies]);
            setPrivateMessages(sortedRepliesSoFar);
            setMoreReplies(repliesStream.length > sortedRepliesSoFar.length);
            return response;
          }
        }
        throw new Error('Failed to get more replies for single topic.');
      } catch (err) {
        console.log('Error while loading more replies');
      }
    } else {
      setMoreReplies(false);
    }
    return null;
  };
  const updatePost = async () => {
    const response = await topicsAPI.getSingleTopic(envState.base_url, authState.api_key, privateMessageId, true);
    if (response && response.post_stream) {
      const {
        posts,
        stream
      } = response.post_stream;
      // new stream contains the old stream AND is longer
      if (_.difference(repliesStream, stream).length === 0 && stream.length > repliesStream.length) setRepliesStream(stream);
      if (posts) {
        const sortedReplies = posts.sort((a, b) => a.post_number - b.post_number);
        let sortedRepliesSoFar = uniqueReplyReducer([...privateMessages, ...sortedReplies]);
        setPrivateMessages(sortedRepliesSoFar);
        scrollToBottom.current = true;
      }
    }
  };
  useEffect(() => {
    if (privateMessageId && postUpdated.topicId === privateMessageId) {
      if (postUpdated !== null && postUpdated !== void 0 && postUpdated.updated) {
        updatePost();
      } else {
        updatePost();
        editPostDispatch({
          type: 'SET_POST_UPDATED',
          payload: {}
        });
      }
    }
  }, [postUpdated]);
  const resetPrivateMessageScreen = () => {
    messagesDispatch({
      type: 'RESET_MESSAGE_USERS'
    });
    setPrivateMessageId(null);
    setTopicTitle(null);
  };
  const renderItem = ({
    item: pmsg
  }) => /*#__PURE__*/React.createElement(PrivateMessage, {
    pmsg: pmsg,
    key: pmsg.id
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(GoBackButton, {
    navigation: props.navigation,
    normalizeUrl: (_props$route2 = props.route) === null || _props$route2 === void 0 || (_props$route2 = _props$route2.params) === null || _props$route2 === void 0 ? void 0 : _props$route2.normalizeUrl,
    my: 1
  }), /*#__PURE__*/React.createElement(VStack, {
    h: envState.pm_screen_height_override || {
      base: 0.66 * Dimensions.get('window').height,
      xs: 0.8 * Dimensions.get('window').height,
      lg: Dimensions.get('window').height
    }
  }, /*#__PURE__*/React.createElement(Center, null, /*#__PURE__*/React.createElement(SearchRecipient, {
    navigation: props.navigation,
    setPrivateMessageId: setPrivateMessageId,
    dmUsers: dmUsers,
    setDMUsers: setDMUsers
  })), /*#__PURE__*/React.createElement(Divider, {
    style: {
      marginTop: '5px',
      marginBottom: '5px'
    }
  }), messagesState.message_users.length ? /*#__PURE__*/React.createElement(Center, null, /*#__PURE__*/React.createElement(Text, {
    paddingBottom: "4",
    alignSelf: "center",
    fontSize: 'xl'
  }, topicTitle)) : null, /*#__PURE__*/React.createElement(Center, {
    w: '100%',
    h: "55%",
    overflow: "auto"
  }, privateMessageId && privateMessages && privateMessages.length > 0 ? /*#__PURE__*/React.createElement(Box, {
    w: "100%",
    flexShrink: "1"
  }, moreReplies && /*#__PURE__*/React.createElement(Pressable, {
    onPress: () => {
      scrollToBottom.current = false;
      loadMoreReplies();
    }
  }, /*#__PURE__*/React.createElement(Center, null, "Load older posts...")), /*#__PURE__*/React.createElement(FlatList, {
    ref: PMRef,
    onContentSizeChange: () => {
      if (scrollToBottom.current) PMRef.current.scrollToEnd();
    },
    data: privateMessages,
    renderItem: renderItem,
    keyExtractor: pmsg => pmsg.id.toString()
  })) : /*#__PURE__*/React.createElement(Text, null, "Start a conversation!"), topic && /*#__PURE__*/React.createElement(Banner, {
    type: 'reply',
    refresh: refreshReplyBanner,
    close: closeReplyBanner,
    topicId: topic.id,
    topicIdList: topicIdListForReplies
  })), /*#__PURE__*/React.createElement(Divider, {
    style: {
      marginTop: '5px',
      marginBottom: '5px'
    }
  }), authState.dm_option_on ? /*#__PURE__*/React.createElement(CreateMessageBox, {
    topicId: privateMessageId,
    users: dmUsers,
    topicTitle: topicTitle,
    trackForum: props.trackForum,
    setPrivateMessageId: setPrivateMessageId
  }) : /*#__PURE__*/React.createElement(Center, null, /*#__PURE__*/React.createElement(Text, {
    color: colors.privateMessage.notEnabled
  }, LANGUAGES.DMS_NOT_ENABLED), /*#__PURE__*/React.createElement(CommunicationPreferencesButton, {
    toggleProfileModal: props.toggleProfileModal
  }))));
};
export default PrivateMessageScreen;
//# sourceMappingURL=PrivateMessageScreen.js.map